import React, { useState } from 'react';
import { Alert, Paper, TextField, Button, Box, Typography, Link, InputAdornment, IconButton } from '@mui/material';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { register } from '../services/auth';
import { useNavigate } from 'react-router-dom';
import smartClickLogo from '../images/smartclick-black-min.png';
import robotPencil from '../images/robot-pencil.png';
import Loading from './widgets/Loading';
import PlatformsBackground from './widgets/PlatformsBackground';
import { useTranslation } from 'react-i18next';

function SignUp() {
	const { t } = useTranslation();
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();

	const [errorMessage, setErrorMessage] = useState('');

	const handleTogglePassword = () => { setShowPassword(!showPassword); }

	const handleRegister = async () => {
		setIsLoading(true);
		setErrorMessage('');
		try {
			if (!name || !email || !password) {
				throw new Error(t('signup.fillAllFields'));
			}
			await register(name, email, password);
			navigate('/login');
		} catch (error) {
			setErrorMessage(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: "100vh",
            bgcolor: "rgb(242, 242, 242)",
            padding: 2, 
			}}
		>
			<PlatformsBackground/>
			<Paper 
				sx={{
					textAlign: "center",
					padding: 4,
					justifyContent: "center",
					maxWidth: 400,
					zIndex: 1,
				}}
				elevation={3}
			>
				{/* Logo */}
				<Box
					component="img"
					src={smartClickLogo}
					alt="Logo"
					sx={{ marginBottom: 4 }}
				/>

				{/* Welcome Message */}
				<Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 1 }}>
					{t('signup.title')}
				</Typography>
				<Typography variant="body1" sx={{ color: "gray", marginBottom: 4 }}>
					{t('signup.subtitle')}
				</Typography>

				{/* Name Field */}
				<TextField
					label={t('signup.nameLabel')}
					placeholder={t('signup.namePlaceholder')}
					variant="outlined"
					fullWidth
					value={name}
					onChange={(e) => setName(e.target.value)}
					size="small"
					sx={{ marginBottom: 2, maxWidth: 400 }}
				/>

				{/* Email Field */}
				<TextField
				label={t('signup.emailLabel')}
				placeholder={t('signup.emailPlaceholder')}
				variant="outlined"
				fullWidth
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				size="small"
				error={!!email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
				helperText={!!email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? t('signup.invalidEmail') : ""}
				sx={{ marginBottom: 2, maxWidth: 400 }}
				/>

				{/* Password Field */}
				<TextField
				label={t('signup.passwordLabel')}
				type={showPassword ? "text" : "password"}
				variant="outlined"
				fullWidth
				size="small"
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				error={
					!!password &&
					!(
					password.length >= 6 &&
					/[!@#$%^&*(),.?":{}|<>]/.test(password) &&
					/\d/.test(password) &&
					/[A-Z]/.test(password) &&
					/[a-z]/.test(password)
					)
				}
				helperText={
					!!password &&
					!(
					  password.length >= 6 &&
					  /[!@#$%^&*(),.?":{}|<>]/.test(password) &&
					  /\d/.test(password) &&
					  /[A-Z]/.test(password) &&
					  /[a-z]/.test(password)
					) ? (
					  <div>
						<div>{t('signup.passwordRequirementsTitle')}</div>
						<ul style={{ margin: 0, paddingLeft: "20px" }}>
						  <li>{t('signup.passwordLength')}</li>
						  <li>{t('signup.passwordNumber')}</li>
						  <li>{t('signup.passwordUppercase')}</li>
						  <li>{t('signup.passwordLowercase')}</li>
						  <li>{t('signup.passwordSpecialChar')}</li>
						</ul>
					  </div>
					) : ""
				  }
				sx={{ marginBottom: 4, maxWidth: 400 }}
				InputProps={{
					endAdornment: (
					<InputAdornment position="end">
						<IconButton onClick={handleTogglePassword} edge="end">
						{showPassword ? <VisibilityOff /> : <Visibility />}
						</IconButton>
					</InputAdornment>
					),
				}}
				/>	

				{errorMessage && (
					<Alert severity="error" sx={{ marginBottom: 2 }}>
						{errorMessage}
					</Alert>
				)}

				{/* Login Button */}
				<Button
					variant="contained"
					color="primary"
					sx={{ width: "100%", maxWidth: 400, marginBottom: 2 }}
					onClick={handleRegister}
				>
					{t('signup.registerButton')}
				</Button>

				{/* Links */}
				<Box sx={{ textAlign: "center", marginTop: 2 }}>
					<Typography variant="body2" sx={{ marginBottom: 1 }}>
					{t('signup.alreadyHaveAccount')} {" "}
					<Link href="/login" underline="hover">
						{t('signup.loginLink')}
					</Link>
					</Typography>
					<Typography variant="body2">
					{t('signup.forgotPassword')} {" "}
					<Link href="/reset-password" underline="hover">
						{t('signup.forgotPasswordLink')}
					</Link>
					</Typography>
				</Box>
			</Paper>

			<Loading 
				text={t('signup.loadingMessage')}  
				isLoading={isLoading} 
			/>
		</Box>
	);
}

export default SignUp;